import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { PenSquare, Settings2, Search, RefreshCw, Inbox, Clock } from "lucide-react";
import { MailList } from "./MailList";
import { MailReader } from "./MailReader";
import { MailCompose } from "./MailCompose";
import { MailSetup } from "./MailSetup";

const FOLDERS = [["inbox", "Inbox"], ["sent", "Sent"], ["trash", "Trash"]];

export const StaffMail = ({ onCountChange, openRequest, liveUnread }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [boxes, setBoxes] = useState(null);
  const [mailbox, setMailbox] = useState("all");
  const [folder, setFolder] = useState("inbox");
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [compose, setCompose] = useState(null);
  const [setup, setSetup] = useState(false);
  const [waiting, setWaiting] = useState(false);

  const loadBoxes = useCallback(() => api.get("/staff/mail/mailboxes").then((r) => {
    setBoxes(r.data);
    onCountChange?.(r.data.total_unread || 0);
  }).catch(() => setBoxes({ mailboxes: [] })), [onCountChange]);
  const loadList = useCallback(() => api.get("/staff/mail", { params: { mailbox, folder, q, waiting: folder === "inbox" && waiting } })
    .then((r) => setList(r.data)).catch(() => setList({ items: [], total: 0 })), [mailbox, folder, q, waiting]);

  useEffect(() => { loadBoxes(); }, [loadBoxes]);
  useEffect(() => { setList(null); const t = setTimeout(loadList, q ? 300 : 0); return () => clearTimeout(t); }, [loadList, q]);
  const refresh = () => { loadBoxes(); loadList(); };
  useEffect(() => { if (openRequest) { setSetup(false); setMailbox("all"); setFolder("inbox"); setOpenId(openRequest.id); } }, [openRequest]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (liveUnread !== undefined && !openId) refresh(); }, [liveUnread]);

  const mine = (boxes?.mailboxes || []).filter((b) => !["unassigned", "assigned"].includes(b.address));
  const hasAssigned = (boxes?.mailboxes || []).some((b) => b.address === "assigned");
  if (setup) return <MailSetup onClose={() => { setSetup(false); refresh(); }} />;
  if (boxes && !mine.length && !isAdmin && !hasAssigned) {
    return <div data-testid="mail-no-mailbox" className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500">
      <Inbox className="mx-auto mb-3 text-slate-300" size={36} />You don't have a @critterposts.ca address yet. Ask an admin to set one up for you.</div>;
  }
  return (
    <div className="grid lg:grid-cols-12 gap-5" data-testid="staff-mail">
      <aside className="lg:col-span-3 space-y-3">
        <Button onClick={() => setCompose({})} disabled={!mine.length} data-testid="mail-compose-btn" className="w-full rounded-xl bg-[#F5551E] hover:bg-[#e04a15] gap-2"><PenSquare size={16} /> New email</Button>
        <div className="bg-white border border-slate-200 rounded-2xl p-2">
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold px-2 pt-1 pb-2">Mailboxes</p>
          {[{ address: "all", display_name: "All my mail", unread: boxes?.total_unread }, ...(boxes?.mailboxes || [])].map((b) => (
            <button key={b.address} onClick={() => { setMailbox(b.address); setOpenId(null); }} data-testid={`mail-box-${b.address}`}
              className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-xl text-sm transition-colors ${mailbox === b.address ? "bg-[#FFF4F0] text-[#F5551E] font-semibold" : "hover:bg-slate-50 text-slate-700"}`}>
              <span className="truncate">{["all", "unassigned", "assigned"].includes(b.address) ? b.display_name : b.address}</span>
              {b.unread > 0 && <span className="ml-2 bg-[#F5551E] text-white text-[10px] font-bold rounded-full h-5 min-w-5 px-1.5 inline-flex items-center justify-center">{b.unread}</span>}
            </button>
          ))}
        </div>
        {isAdmin && <Button variant="outline" onClick={() => setSetup(true)} data-testid="mail-setup-btn" className="w-full rounded-xl gap-2"><Settings2 size={16} /> Email setup & addresses</Button>}
        {boxes && !boxes.configured && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="mail-not-configured">Email isn't connected yet. An admin needs to finish Email setup.</p>}
      </aside>
      <section className="lg:col-span-9 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-white border border-slate-200 rounded-xl p-1">
            {FOLDERS.map(([v, l]) => <button key={v} onClick={() => { setFolder(v); setOpenId(null); }} data-testid={`mail-folder-${v}`}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${folder === v ? "bg-[#12234A] text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>)}
          </div>
          {folder === "inbox" && (
            <button onClick={() => { setWaiting(!waiting); setOpenId(null); }} data-testid="mail-waiting-filter"
              className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border text-sm transition-colors ${waiting ? "border-[#F5551E] bg-[#FFF4F0] text-[#F5551E] font-semibold" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
              <Clock size={15} /> Needs reply
              {boxes?.waiting_overdue > 0 && <span className="bg-red-600 text-white text-[10px] font-bold rounded-full h-5 min-w-5 px-1.5 inline-flex items-center justify-center" title="Waiting over a day" data-testid="mail-overdue-count">{boxes.waiting_overdue}</span>}
            </button>
          )}
          <div className="relative flex-1 min-w-[180px]"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email" className="pl-9 rounded-xl" data-testid="mail-search-input" /></div>
          <Button variant="outline" size="icon" onClick={refresh} className="rounded-xl" data-testid="mail-refresh-btn"><RefreshCw size={16} /></Button>
        </div>
        {openId
          ? <MailReader id={openId} onBack={() => { setOpenId(null); refresh(); }} onReply={setCompose} onRead={loadBoxes} />
          : <MailList data={list} folder={folder} onOpen={setOpenId} onChanged={refresh} />}
      </section>
      {compose && <MailCompose draft={compose} mailboxes={mine} onClose={() => setCompose(null)} onSent={() => { setCompose(null); refresh(); }} />}
    </div>
  );
};
